// 📄 clienteConexionService.ts
// Configuración/credenciales de conexión por cliente, para las apps
// soportadas (CIG, SUNAT, APIs). El guardado/lectura de credenciales
// NUNCA pasa por Firestore directo desde el navegador — solo vía
// onehubApi (/cliente-conexion), protegido por el mismo middleware de
// auth que el resto de la API (ver functions/routes/clienteConexion.js).
import { getAuth } from "firebase/auth";

export type AppCredenciales = 'cig' | 'sunat' | 'apis';

export interface SystemIds {
  dev: string;
  qas: string;
  prd: string;
}

// Cuál de DEV/QAS es el System ID por defecto para el ambiente test
// (extensión de Chrome, Fase 3a — ver functions/services/systemIdResolver.js).
export type SystemIdTestPorDefecto = 'dev' | 'qas' | '';

export interface CigConexionConfig {
  usuario: string;
  tieneCredencialesGuardadas: boolean;
  dataCenter: string;
  erpType: 'onprem' | 'cloud' | '';
  systemIds: SystemIds;
  systemIdTestPorDefecto: SystemIdTestPorDefecto;
  // ANIDs de Business Network del comprador (base, sin -T). Solo lectura acá:
  // se editan con guardarAnidsCliente (solo admin de plataforma).
  buyerAnids: string[];
}

export interface GuardarCigConexionData {
  dataCenter: string;
  erpType: 'onprem' | 'cloud' | '';
  systemIds: SystemIds;
  systemIdTestPorDefecto?: SystemIdTestPorDefecto;
  usuario?: string;
  contraseña?: string;
}

export interface SunatCredenciales {
  usuario: string;
  url: string;
  tieneCredencialesGuardadas: boolean;
}

export interface GuardarSunatCredenciales {
  usuario?: string;
  contraseña?: string;
  url?: string;
}

export interface ApiEndpointParam {
  clave: string;
  valor: string;
}

export type ApiEndpointMetodo = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

// Un endpoint se arma concatenando la URL Base y la API (ambas de la
// ApiDefinition dueña, ver abajo — se piden una sola vez por API, no por
// endpoint) + su propio path, p. ej. "vendorDataRequests" o
// "AttachmentContentSet". Una misma API suele tener varios (uno por
// endpoint que consume), por eso viven en una lista.
export interface ApiEndpointConfig {
  id: string;
  nombre: string;
  endpoint: string;
  metodo: ApiEndpointMetodo;
  bodyEjemplo: string;
  params: ApiEndpointParam[];
  headers: ApiEndpointParam[];
  // Variables del path tipo ":vendorId" en endpoint.endpoint (ver
  // utils/pathVariables.ts) — opcional porque endpoints guardados antes
  // de este campo no lo tienen; se trata como [] en ese caso (mismo
  // criterio que ya existe para headers, ver EndpointConfigCard.tsx).
  pathVariables?: ApiEndpointParam[];
}

// Sistema al que pertenece la API: determina qué credenciales se piden.
// Ariba usa OAuth2 (apiKey/oauthSecret + Client ID/Data Center/Realm/ANID
// + URL de token); S/4HANA Cloud usa autenticación básica (usuario +
// contraseña, sin token). Agregar un sistema nuevo es agregar un valor
// acá + su entrada en TIPOS_API (ver apiTiposConfig.ts) + su definición
// de campos en configService.js (backend).
export type ApiTipoId = 'ariba' | 's4hana';

// Modo de autenticación de una API — antes se derivaba 1:1 del `tipo`
// (Ariba = siempre OAuth2, S/4HANA = siempre básica, ver
// apiTiposConfig.ts). Se independizó porque "Flow Extension API" es
// Ariba pero NO usa OAuth2 (pide Basic Auth manual + headers fijos, sin
// token) — ahora es un campo elegible por API (ver radio button en
// ApiDefinitionForm.tsx/ApiTemplateForm.tsx), con el valor de `tipo`
// como default sugerido, no como regla fija. Vive acá (no en
// apiTiposConfig.ts, que en cambio la importa) para que ApiDefinition
// pueda declararla sin crear un import circular.
export type ApiAuthTipo = 'oauth' | 'basic';

// Cada API que un cliente consume tiene sus propias credenciales (no se
// comparten entre APIs distintas), su propia URL Base + API (comunes a
// todos sus endpoints, ver arriba) y —solo para APIs OAuth como Ariba—
// su propia URL de token fija, p. ej.
// "https://api-eu.ariba.com/v2/oauth/token?grant_type=openapi_2lo". Un
// cliente puede tener varias APIs configuradas, de distintos sistemas,
// cada una con su propia lista de endpoints.
export interface ApiDefinition {
  id: string;
  tipo: ApiTipoId;
  authTipo: ApiAuthTipo;
  nombre: string;
  protocolo: string;
  ambiente: string;
  urlBase: string;
  api: string;
  // OAuth2 (Ariba)
  apiKey: string;
  oauthSecret: string;
  clientId: string;
  dataCenter: string;
  realm: string;
  anid: string;
  tokenUrl: string;
  // Grant type del token OAuth2 (2LO). En blanco = "openapi_2lo" (el que
  // usa la mayoría de OpenAPIs de Ariba); algunas APIs más nuevas piden
  // "client_credentials" — se deja configurable por API en vez de fijo.
  grantType: string;
  // Autenticación básica (S/4HANA Cloud)
  usuario: string;
  contraseña: string;
  tieneCredencialesGuardadas: boolean;
  endpoints: ApiEndpointConfig[];
}

export interface ApisCredenciales {
  apis: ApiDefinition[];
}

export interface GuardarApisCredenciales {
  apis: ApiDefinition[];
}

const getToken = async (): Promise<string> => {
  const user = getAuth().currentUser;
  if (!user) throw new Error("No se detectó una sesión activa. Por favor, reingresa al sistema.");
  return user.getIdToken();
};

const post = async (path: string, body: object) => {
  const token = await getToken();
  const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify(body)
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Error ${response.status} al comunicarse con el servidor.`);
  }
  return data;
};

export const obtenerConfigCliente = async <T,>(clienteId: string, app: AppCredenciales): Promise<T> => {
  return post("/cliente-conexion/obtener", { clienteId, app });
};

export const guardarConfigCliente = async (clienteId: string, app: AppCredenciales, data: object): Promise<void> => {
  await post("/cliente-conexion/guardar", { clienteId, app, ...data });
};

// APIs "personales": cuando se usa Pruebas APIs desde el Home (sin
// proyecto/cliente activo, ver AribaApiTester.tsx), lo que el usuario
// arma se guarda a su propia cuenta en vez de a un cliente — mismo
// modelo de datos (ApiDefinition), doc distinto en el backend
// (usuarios/{uid}/credenciales/apis, ver routes/miConexion.js).
export const obtenerMisApis = async (): Promise<ApisCredenciales> => {
  return post("/mi-conexion/obtener", {});
};

export const guardarMisApis = async (apis: ApiDefinition[]): Promise<void> => {
  await post("/mi-conexion/guardar", { apis });
};

// Código de confirmación por cliente para el modal "Confirmar Producción"
// (ver EstatusFacturas.tsx). El código nunca se lee de vuelta: /estado solo
// dice si ya hay uno guardado, y /verificar solo responde true/false.
// Solo admin de plataforma (el backend lo exige). Reemplaza la lista completa
// y mantiene el índice ANID -> cliente; falla si un ANID ya es de otro cliente.
export const guardarAnidsCliente = async (clienteId: string, buyerAnids: string[]): Promise<string[]> => {
  const resp = await post("/cliente-conexion/anids/guardar", { clienteId, buyerAnids });
  return resp.buyerAnids;
};

export const obtenerEstadoCodigoProduccion = async (clienteId: string): Promise<{ tieneCodigoGuardado: boolean }> => {
  return post("/cliente-conexion/prd-confirm/estado", { clienteId });
};

export const guardarCodigoProduccion = async (clienteId: string, codigo: string): Promise<void> => {
  await post("/cliente-conexion/prd-confirm/guardar", { clienteId, codigo });
};

export const verificarCodigoProduccion = async (clienteId: string, codigo: string): Promise<boolean> => {
  const { valido } = await post("/cliente-conexion/prd-confirm/verificar", { clienteId, codigo });
  return valido;
};
